import { Nub } from "../core";
import type { $ } from "../dom";
import { mod } from "../maths";

export type TemplateOptions = {
  defaultKey?: string;

  onTemplateInit: (element: Element, index: number) => string | false;
};

export type InitElementCallback<TItem> = (
  item: TItem,
  $clone: $,
) => boolean | InsertOptions | void;

export type InsertOptions = {
  index?: number;
  relativeTo?: "itinerary" | "global";
};

export class Template extends Nub {
  public readonly defaultKey: string | undefined;

  private readonly itinerary: (string | Element)[] = [];
  private readonly templateByKey: Record<string, $>;

  constructor($root: $, options: TemplateOptions) {
    super($root, {
      type: "single",
      name: "template",
    });

    this.templateByKey = {};

    this.$root.children.each((element, index) => {
      const $child = this.$root.make(element);

      const optionKey = options.onTemplateInit(element, index);

      if (optionKey === false) {
        this.itinerary.push(element);

        return;
      }

      const key = optionKey;

      if (this.hasKey(key)) {
        console.warn(`[template] key "${key}" already exists`, this.$root);
      } else {
        this.templateByKey[key] = $child;

        this.itinerary.push(key);

        $child.remove();
      }
    });

    if (!Object.keys(this.templateByKey).length) {
      console.warn(`[template] no key found`, this.$root);
    }

    this.defaultKey = options.defaultKey
      ? options.defaultKey
      : Object.keys(this.templateByKey).at(0);
  }

  $clones(key?: string) {
    if (key === undefined) {
      return this.$root.children.eq(this.$root._brk("clone"));
    }

    if (!this.hasKey(key)) {
      throw new Error(`[template] key "${key}" not found`);
    }

    return this.$root.children.eq(this.$root._brkk(key, "clone"));
  }

  get keys() {
    return Object.keys(this.templateByKey);
  }

  $template(key: string | undefined = this.defaultKey) {
    if (key === undefined || !this.hasKey(key)) {
      throw new Error(`[template] key "${key}" not found`);
    }

    return this.templateByKey[key];
  }

  hasKey(key?: string) {
    if (key === undefined) {
      return this.keys.length > 0;
    }

    return this.templateByKey[key] !== undefined;
  }

  length(key?: string) {
    return this.$clones(key).length;
  }

  insert(insertOptions: InsertOptions & { key?: string } = {}) {
    const { key, ...options } = insertOptions;

    const { cloneKey, $clone } = this._clone(key);

    return this._insert(cloneKey, $clone, options);
  }

  empty(key?: string) {
    return this.$clones(key).remove();
  }

  remove(index: number, key?: string) {
    return this.$clones(key).eq(index).remove();
  }

  fromObject<TItem>(
    item: TItem,
    callback: InitElementCallback<TItem>,
    key?: string,
  ) {
    return this._fromObject(key, item, callback);
  }

  fromArray<TItem>(
    array: TItem[],
    handler: (
      item: TItem,
      $clone: $,
      index: number,
    ) => boolean | InsertOptions | void,
    key?: string,
  ) {
    const $clones = this.$root.make();

    for (let i = 0; i < array.length; ++i) {
      $clones.push(
        this._fromObject(key, array[i], (item: TItem, $clone: $) =>
          handler(item, $clone, i),
        ),
      );
    }

    return $clones;
  }

  fromValues<TItem>(
    object: Record<string, TItem>,
    callback: InitElementCallback<TItem>,
    key: string | undefined = this.defaultKey,
  ) {
    return this.fromArray(Object.values(object), callback, key);
  }

  fromKeys(
    object: Record<string, unknown>,
    callback: InitElementCallback<string>,
    key: string | undefined = this.defaultKey,
  ) {
    return this.fromArray(Object.keys(object), callback, key);
  }

  for(
    count: number,
    callback: InitElementCallback<number> = () => true,
    key: string | undefined = this.defaultKey,
  ) {
    const $clones = this.$root.make();

    for (let i = 0; i < count; ++i) {
      $clones.push(this._fromObject(key, i, callback));
    }

    return $clones;
  }

  private _fromObject<TItem>(
    key: string | undefined,
    item: TItem,
    callback: InitElementCallback<TItem>,
  ) {
    const { $clone, cloneKey } = this._clone(key, item);

    const insert = callback(item, $clone) ?? true;

    if (insert) {
      this._insert(cloneKey, $clone, insert === true ? undefined : insert);
    }

    return $clone;
  }

  private _clone(key: string | undefined = this.defaultKey, item?: unknown) {
    if (key === undefined || !this.hasKey(key)) {
      throw new Error(`[template] key "${key}" not found`);
    }

    const $clone = this.$template(key).clone(true);

    $clone.rk("template", null);
    $clone.rk("clone", key);

    if (item !== undefined) {
      $clone.data("template:item", item);
    }

    return {
      cloneKey: key,
      $clone,
    };
  }

  private _insert(cloneKey: string, $clone: $, options: InsertOptions = {}) {
    let { index = -1, relativeTo = "itinerary" } = options;

    const startIndex =
      relativeTo === "itinerary"
        ? this.itinerary
            .slice(0, this.itinerary.indexOf(cloneKey))
            .reduce(
              (index, key) =>
                index + (key instanceof Element ? 1 : this.length(key)),
              0,
            )
        : 0;

    const endIndex =
      relativeTo === "itinerary"
        ? startIndex + this.length(cloneKey)
        : this.$root.children.length;

    const length = endIndex - startIndex;

    const relativeIndex =
      startIndex + (length > 0 ? mod(index, length + 1) : 0);

    if (relativeIndex < this.$root.children.length) {
      this.$root.children.eq(relativeIndex).before($clone);
    } else {
      this.$root.append($clone);
    }

    return $clone;
  }
}
